import {
  Button,
  darken,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Theme,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import { isDarkTheme } from '../../../../eliot-components/src/theme';
import { DeviceTileProps, DeviceTileSkeletonProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  label: {
    display: 'inline-block',
    backgroundColor: theme.palette.action.hover,
    minWidth: '70px',
    margin: theme.spacing(0, 1, 0, 0),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    textAlign: 'left',
  },

  skeleton: {
    margin: theme.spacing(0, 0, 1, 0),
    borderRadius: '4px',
  },

  labelContainer: {
    margin: theme.spacing(0, 0.5, 0.5, 0.5),
    '&:first-child': {
      marginTop: theme.spacing(0.5),
    },
  },

  tile: {
    backgroundColor: isDarkTheme(theme)
      ? darken(theme.palette.background.paper, 0.2)
      : theme.palette.background.paper,
  },
}));

function DeviceTile({
  device,
  className,
  unlinkHandler,
  isGateway,
  disableUnlink,
}: DeviceTileProps) {
  const classes = useStyles();

  const unlinkAll = useCallback(async () => {
    if (isGateway && unlinkHandler && device.sensors) {
      await unlinkHandler(device.sensors);
    }
  }, [isGateway, unlinkHandler, device]);

  const renderUnlinkButton = () => {
    if (isGateway) {
      return (
        <Button
          color="primary"
          onClick={() => unlinkAll()}
          disabled={disableUnlink}
        >
          Unlink all
        </Button>
      );
    } else if (unlinkHandler) {
      return (
        <Button
          color="primary"
          onClick={() => unlinkHandler([device])}
          disabled={disableUnlink}
        >
          Unlink
        </Button>
      );
    }
  };

  return (
    <Paper className={clsx(classes.tile, className)}>
      <Grid container direction="column">
        {device && (
          <Grid item className={classes.labelContainer}>
            <span className={classes.label}>SN:</span> {device.serialNumber}
          </Grid>
        )}
        {device && (
          <Grid item className={classes.labelContainer}>
            <span className={classes.label}>Model:</span> {device.model}
          </Grid>
        )}
        <Divider />
        {unlinkHandler ? (
          <Grid item container justifyContent="flex-end">
            {renderUnlinkButton()}
          </Grid>
        ) : null}
      </Grid>
    </Paper>
  );
}

export function DeviceTileSkeleton({ repeat = 1 }: DeviceTileSkeletonProps) {
  const classes = useStyles();
  const reps = new Array(repeat).fill(1);
  return (
    <Grid container direction="column">
      {reps.map((_, index) => (
        <Skeleton
          data-testid="device-tile-skeleton"
          key={index}
          variant="rect"
          width="100%"
          height={100}
          className={classes.skeleton}
        />
      ))}
    </Grid>
  );
}

export default DeviceTile;
